import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ActivityFeedItem as ActivityFeedItemType } from '../../types/activity';
import { ContactChanges } from '../../types/contact';
import { Button, IconButton } from '../ui/Button';
import { EyeOffIcon } from '../ui/icons';
import { formatRelativeTime } from '../../utils/dateUtils';
import { GroupedActivityFeedItem } from './GroupedActivityFeedItem';
import { UpcomingMeetingsStory } from './UpcomingMeetingsStory';
import styles from './ActivityFeedItem.module.css';

interface ActivityFeedItemProps {
  activity: ActivityFeedItemType;
  /** Callback when user wants to hide a contact from the feed */
  onHideContact?: (contactId: string, contactName: string) => void;
  /** When true, renders in a more compact style for nested display within groups */
  isNested?: boolean;
}

/**
 * Get the icon for an activity type
 */
const getActivityIcon = (type: ActivityFeedItemType['type']): string => {
  switch (type) {
    case 'contact_created':
      return '👤';
    case 'contact_edited':
      return '✏️';
    case 'contact_merged':
      return '🔀';
    case 'task_created':
      return '📝';
    case 'task_completed':
      return '✅';
    case 'task_updated':
      return '🔄';
    case 'calendar_event_update':
      return '📅';
    case 'birthday_today':
      return '🎂';
    case 'security_new_access':
      return '🔐';
    case 'ai_credits_exhausted':
      return '⛽';
    case 'gmail_token_expired':
      return '⚠️';
    case 'upcoming_meetings':
      return '📅';
    default:
      return '📌';
  }
};

const getActivityLabel = (type: ActivityFeedItemType['type']): string => {
  switch (type) {
    case 'contact_created':
      return 'New contact';
    case 'contact_edited':
      return 'Contact update';
    case 'contact_merged':
      return 'Contact merge';
    case 'task_created':
      return 'New task';
    case 'task_completed':
      return 'Task done';
    case 'task_updated':
      return 'Task update';
    case 'calendar_event_update':
      return 'Calendar';
    case 'birthday_today':
      return 'Birthday';
    case 'security_new_access':
      return 'Security';
    case 'ai_credits_exhausted':
      return 'Automation';
    case 'gmail_token_expired':
      return 'Gmail';
    case 'upcoming_meetings':
      return 'Meetings';
    default:
      return 'Activity';
  }
};

const getActivityToneClass = (type: ActivityFeedItemType['type']): string => {
  switch (type) {
    case 'task_created':
    case 'task_completed':
    case 'task_updated':
      return 'kindTask';
    case 'calendar_event_update':
    case 'upcoming_meetings':
      return 'kindCalendar';
    case 'birthday_today':
      return 'kindBirthday';
    case 'security_new_access':
      return 'kindSystem';
    case 'ai_credits_exhausted':
      return 'kindWarning';
    case 'gmail_token_expired':
      return 'kindAlert';
    default:
      return 'kindContact';
  }
};

/**
 * Get a human-readable label for a field name
 */
const getFieldLabel = (field: string): string => {
  const labels: Record<string, string> = {
    firstName: 'first name',
    lastName: 'last name',
    company: 'company',
    title: 'title',
    linkedInUrl: 'LinkedIn URL',
    emails: 'emails',
    phones: 'phones',
    tags: 'tags',
  };
  return labels[field] || field;
};

const getAgentLabel = (agentName: string): string => {
  const labels: Record<string, string> = {
    EMAIL_ANALYSIS_AGENT: 'Email Analysis',
    EMAIL_TRIAGE_AGENT: 'Email Triage',
  };

  if (labels[agentName]) {
    return labels[agentName];
  }

  return agentName
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\bagent\b/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Format a value for display
 */
const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '(empty)';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '(none)';
    }
    // Handle email/phone arrays
    if (value[0] && typeof value[0] === 'object') {
      if ('email' in value[0]) {
        return value.map((e: any) => e.email).join(', ');
      }
      if ('phone' in value[0]) {
        return value.map((p: any) => p.phone).join(', ');
      }
    }
    return value.join(', ');
  }
  if (typeof value === 'string') {
    return value;
  }
  return String(value);
};

/**
 * Get a summary of changes for display
 */
const getChangeSummary = (changes: ContactChanges): string => {
  const changedFields = Object.keys(changes);
  if (changedFields.length === 0) {
    return 'made changes';
  }
  if (changedFields.length === 1) {
    return `updated ${getFieldLabel(changedFields[0])}`;
  }
  if (changedFields.length === 2) {
    return `updated ${getFieldLabel(changedFields[0])} and ${getFieldLabel(changedFields[1])}`;
  }
  return `updated ${getFieldLabel(changedFields[0])} and ${changedFields.length - 1} other fields`;
};

export const ActivityFeedItem: React.FC<ActivityFeedItemProps> = ({
  activity,
  onHideContact,
  isNested = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const activityToneClass = styles[getActivityToneClass(activity.type)];
  const renderMetaRow = (label: string = getActivityLabel(activity.type)) => (
    <div className={styles.activityMeta}>
      <span className={`${styles.activityKind} ${activityToneClass}`}>{label}</span>
      <span className={styles.activityTime}>{formatRelativeTime(activity.timestamp)}</span>
    </div>
  );

  const handleHideContact = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (activity.contact && onHideContact) {
      onHideContact(activity.contact.id, activity.contact.name);
    }
  };

  // Handle grouped contact edits
  if (activity.type === 'contact_edited_group' && activity.groupedContact) {
    return <GroupedActivityFeedItem groupedContact={activity.groupedContact} onHideContact={onHideContact} />;
  }

  const renderContactActivity = () => {
    if (!activity.contact) return null;

    const { contact } = activity;
    const hasChanges = contact.changes && Object.keys(contact.changes).length > 0;
    const hasContext = contact.company || contact.title || (contact.tags && contact.tags.length > 0);

    return (
      <div className={styles.activityContent}>
        {renderMetaRow()}
        <div className={styles.activityHeader}>
          <span className={styles.activityIcon}>{getActivityIcon(activity.type)}</span>
          <div className={styles.activityText}>
            <span className={styles.activityDescription}>
              {activity.type === 'contact_edited' ? (
                <>
                  <Link to={`/contacts/${contact.id}`} className={styles.entityLink}>
                    {contact.name}
                  </Link>
                  {' '}
                  {hasChanges ? getChangeSummary(contact.changes!) : 'was updated'}
                </>
              ) : (
                <>
                  <Link to={`/contacts/${contact.id}`} className={styles.entityLink}>
                    {contact.name}
                  </Link>
                  {' '}was created
                </>
              )}
            </span>
            
            {/* Contact context section */}
            {hasContext && (
              <div className={styles.contactContext}>
                {(contact.title || contact.company) && (
                  <span className={styles.contactRole}>
                    {contact.title}
                    {contact.title && contact.company && ' at '}
                    {contact.company}
                  </span>
                )}
                {contact.tags && contact.tags.length > 0 && (
                  <div className={styles.contactTags}>
                    {contact.tags.map((tag, index) => (
                      <span key={index} className={styles.tagPill}>{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {onHideContact && !isNested && (
            <IconButton
              icon={<EyeOffIcon size={14} />}
              aria-label={`Hide ${contact.name} from feed`}
              variant="ghost"
              size="sm"
              className={styles.hideButton}
              onClick={handleHideContact}
              title={`Hide ${contact.name} from feed`}
            />
          )}
        </div>

        {hasChanges && (
          <>
            <Button
              variant="ghost"
              size="sm"
              className={styles.expandButton}
              onClick={() => setIsExpanded(!isExpanded)}
              aria-expanded={isExpanded}
            >
              {isExpanded ? 'Hide details' : 'Show details'}
              <span className={styles.expandIcon}>{isExpanded ? '▼' : '▶'}</span>
            </Button>

            {isExpanded && (
              <div className={styles.changeDetails}>
                {Object.entries(contact.changes!).map(([field, change]) => (
                  <div key={field} className={styles.changeItem}>
                    <span className={styles.changeField}>{getFieldLabel(field)}:</span>
                    <div className={styles.changeValues}>
                      <span className={styles.oldValue}>{formatValue(change.from)}</span>
                      <span className={styles.changeArrow}>→</span>
                      <span className={styles.newValue}>{formatValue(change.to)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderTaskActivity = () => {
    if (!activity.task) return null;

    const { task } = activity;

    return (
      <div className={styles.activityContent}>
        {renderMetaRow()}
        <div className={styles.activityHeader}>
          <span className={styles.activityIcon}>{getActivityIcon(activity.type)}</span>
          <div className={styles.activityText}>
            <span className={styles.activityDescription}>
              {activity.type === 'task_created' && (
                <>
                  Created task:{' '}
                  <span className={styles.taskTitle}>{task.title}</span>
                </>
              )}
              {activity.type === 'task_completed' && (
                <>
                  Completed:{' '}
                  <span className={styles.taskTitle}>{task.title}</span>
                </>
              )}
              {activity.type === 'task_updated' && (
                <>
                  Updated task:{' '}
                  <span className={styles.taskTitle}>{task.title}</span>
                </>
              )}
            </span>
          </div>
        </div>
        <div className={styles.taskMeta}>
          <span className={`${styles.categoryBadge} ${styles[`category${task.category.replace(/\s+/g, '')}`]}`}>
            {task.category}
          </span>
        </div>
      </div>
    );
  };

  const renderMergeActivity = () => {
    if (!activity.merge) return null;

    const { merge } = activity;
    const mergeDetails: string[] = [];
    if (merge.emailsMerged > 0) mergeDetails.push(`${merge.emailsMerged} email${merge.emailsMerged > 1 ? 's' : ''}`);
    if (merge.phonesMerged > 0) mergeDetails.push(`${merge.phonesMerged} phone${merge.phonesMerged > 1 ? 's' : ''}`);
    if (merge.tagsMerged > 0) mergeDetails.push(`${merge.tagsMerged} tag${merge.tagsMerged > 1 ? 's' : ''}`);

    return (
      <div className={styles.activityContent}>
        {renderMetaRow()}
        <div className={styles.activityHeader}>
          <span className={styles.activityIcon}>{getActivityIcon(activity.type)}</span>
          <div className={styles.activityText}>
            <span className={styles.activityDescription}>
              Merged{' '}
              <span className={styles.mergeContactName}>{merge.secondaryContactName}</span>
              {' '}into{' '}
              <Link to={`/contacts/${merge.primaryContactId}`} className={styles.entityLink}>
                {merge.primaryContactName}
              </Link>
            </span>
            {mergeDetails.length > 0 && (
              <span className={styles.mergeDetails}>
                Added {mergeDetails.join(', ')}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  /**
   * Format a date range for display (e.g., "Dec 22, 2:00 PM - 3:00 PM")
   */
  const formatEventTime = (startStr?: string, endStr?: string): string | null => {
    if (!startStr) return null;
    
    const start = new Date(startStr);
    const end = endStr ? new Date(endStr) : null;
    
    const dateOptions: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
    };
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
    };
    
    const dateStr = start.toLocaleDateString(undefined, dateOptions);
    const startTimeStr = start.toLocaleTimeString(undefined, timeOptions);
    
    if (end) {
      // Check if same day
      const sameDay = start.toDateString() === end.toDateString();
      const endTimeStr = end.toLocaleTimeString(undefined, timeOptions);
      
      if (sameDay) {
        return `${dateStr}, ${startTimeStr} - ${endTimeStr}`;
      } else {
        const endDateStr = end.toLocaleDateString(undefined, dateOptions);
        return `${dateStr}, ${startTimeStr} - ${endDateStr}, ${endTimeStr}`;
      }
    }
    
    return `${dateStr}, ${startTimeStr}`;
  };

  const renderCalendarActivity = () => {
    if (!activity.calendarEvent) return null;

    const { calendarEvent } = activity;
    const hasEventDetails = !!(calendarEvent.summary || calendarEvent.eventStart || calendarEvent.location);
    
    // Get a human-readable description based on what we know
    const getEventDescription = (): React.ReactNode => {
      if (calendarEvent.summary) {
        // We have event details - show a rich description
        return (
          <>
            <span className={styles.calendarEventTitle}>{calendarEvent.summary}</span>
            {calendarEvent.resourceState === 'update' && ' was updated'}
          </>
        );
      }
      
      // Fallback to resource state description
      switch (calendarEvent.resourceState) {
        case 'sync':
          return 'Calendar sync initiated';
        case 'exists':
          return 'Calendar event created';
        case 'update':
          return 'Calendar updated';
        default:
          return 'Calendar activity';
      }
    };

    const eventTimeStr = formatEventTime(calendarEvent.eventStart, calendarEvent.eventEnd);

    return (
      <div className={styles.activityContent}>
        {renderMetaRow()}
        <div className={styles.activityHeader}>
          <span className={styles.activityIcon}>{getActivityIcon(activity.type)}</span>
          <div className={styles.activityText}>
            <span className={styles.activityDescription}>
              {getEventDescription()}
            </span>
            
            {/* Event details section */}
            {hasEventDetails && (
              <div className={styles.calendarEventDetails}>
                {eventTimeStr && (
                  <span className={styles.calendarEventTime}>
                    🕐 {eventTimeStr}
                  </span>
                )}
                {calendarEvent.location && (
                  <span className={styles.calendarEventLocation}>
                    📍 {calendarEvent.location}
                  </span>
                )}
                {calendarEvent.attendeesCount !== undefined && calendarEvent.attendeesCount > 0 && (
                  <span className={styles.calendarEventAttendees}>
                    👥 {calendarEvent.attendeesCount} attendee{calendarEvent.attendeesCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}
            
            {calendarEvent.calendarEmail && (
              <span className={styles.calendarEmail}>
                {calendarEvent.calendarEmail}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleHideBirthdayContact = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (activity.birthday && onHideContact) {
      onHideContact(activity.birthday.id, activity.birthday.name);
    }
  };

  const renderBirthdayActivity = () => {
    if (!activity.birthday) return null;

    const { birthday } = activity;
    const hasContext = birthday.company || birthday.title || (birthday.tags && birthday.tags.length > 0);

    return (
      <div className={styles.activityContent}>
        {renderMetaRow()}
        <div className={styles.activityHeader}>
          <span className={styles.activityIcon}>{getActivityIcon(activity.type)}</span>
          <div className={styles.activityText}>
            <span className={styles.activityDescription}>
              <span className={styles.birthdayMessage}>Happy Birthday!</span>
              {' '}
              <Link to={`/contacts/${birthday.id}`} className={styles.entityLink}>
                {birthday.name}
              </Link>
              {birthday.age !== undefined && (
                <span className={styles.birthdayAge}> turns {birthday.age} today</span>
              )}
            </span>
            
            {/* Contact context section */}
            {hasContext && (
              <div className={styles.contactContext}>
                {(birthday.title || birthday.company) && (
                  <span className={styles.contactRole}>
                    {birthday.title}
                    {birthday.title && birthday.company && ' at '}
                    {birthday.company}
                  </span>
                )}
                {birthday.tags && birthday.tags.length > 0 && (
                  <div className={styles.contactTags}>
                    {birthday.tags.map((tag, index) => (
                      <span key={index} className={styles.tagPill}>{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {onHideContact && !isNested && (
            <IconButton
              icon={<EyeOffIcon size={14} />}
              aria-label={`Hide ${birthday.name} from feed`}
              variant="ghost"
              size="sm"
              className={styles.hideButton}
              onClick={handleHideBirthdayContact}
              title={`Hide ${birthday.name} from feed`}
            />
          )}
        </div>
      </div>
    );
  };

  /**
   * Get a human-readable label for auth source
   */
  const getAuthSourceLabel = (authSource: string): string => {
    switch (authSource) {
      case 'web_session':
        return 'Web';
      case 'api_session':
        return 'API Token';
      case 'carddav_basic':
        return 'CardDAV';
      default:
        return authSource;
    }
  };

  const renderSecurityActivity = () => {
    if (!activity.security) return null;

    const { security } = activity;
    const isNewIp = security.type === 'new_ip';
    
    // Build location string from geolocation data
    const getLocationString = (): string | null => {
      const parts: string[] = [];
      if (security.city) parts.push(security.city);
      if (security.region) parts.push(security.region);
      if (security.country) parts.push(security.country);
      return parts.length > 0 ? parts.join(', ') : null;
    };
    
    const location = getLocationString();
    
    return (
      <div className={styles.activityContent}>
        {renderMetaRow()}
        <div className={styles.activityHeader}>
          <span className={styles.activityIcon}>{getActivityIcon(activity.type)}</span>
          <div className={styles.activityText}>
            <span className={styles.activityDescription}>
              <span className={styles.securityMessage}>
                {isNewIp ? 'New IP detected' : 'New device/browser detected'}
              </span>
            </span>
            
            <div className={styles.securityDetails}>
              {isNewIp ? (
                <>
                  <span className={styles.securityIp}>
                    IP: {security.ip}
                  </span>
                  {location && (
                    <span className={styles.securityLocation}>
                      📍 {location}
                    </span>
                  )}
                  {security.isp && (
                    <span className={styles.securityIsp}>
                      🌐 {security.isp}
                    </span>
                  )}
                </>
              ) : (
                <span className={styles.securityDevice}>
                  {security.uaFamily}
                </span>
              )}
              <span className={styles.securitySource}>
                Source: {getAuthSourceLabel(security.authSource)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAICreditActivity = () => {
    if (!activity.aiCredits) return null;

    const { aiCredits } = activity;
    const affectedAgents = aiCredits.affectedAgents.map(getAgentLabel);

    return (
      <div className={styles.activityContent}>
        {renderMetaRow()}
        <div className={styles.activityHeader}>
          <span className={styles.activityIcon}>{getActivityIcon(activity.type)}</span>
          <div className={styles.activityText}>
            <span className={styles.activityDescription}>
              <span className={styles.aiCreditsMessage}>
                AI automations paused
              </span>
            </span>

            <div className={styles.aiCreditsSummary}>
              OpenRouter credits ran out, so background AI processing is waiting for more credits.
            </div>

            {affectedAgents.length > 0 && (
              <div className={styles.aiCreditsDetails}>
                {affectedAgents.map((agent) => (
                  <span key={agent} className={styles.aiCreditsChip}>{agent}</span>
                ))}
              </div>
            )}

            <span className={styles.aiCreditsMeta}>
              {aiCredits.failureCount} failed run{aiCredits.failureCount === 1 ? '' : 's'}
            </span>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Get a human-readable label for Gmail token error type
   */
  const getTokenErrorLabel = (errorType: string): string => {
    switch (errorType) {
      case 'token_expired':
        return 'Token expired';
      case 'token_revoked':
        return 'Access revoked';
      case 'refresh_failed':
        return 'Refresh failed';
      default:
        return 'Connection issue';
    }
  };

  const renderGmailTokenActivity = () => {
    if (!activity.gmailToken) return null;

    const { gmailToken } = activity;
    
    return (
      <div className={styles.activityContent}>
        {renderMetaRow()}
        <div className={styles.activityHeader}>
          <span className={styles.activityIcon}>{getActivityIcon(activity.type)}</span>
          <div className={styles.activityText}>
            <span className={styles.activityDescription}>
              <span className={styles.gmailTokenMessage}>
                Gmail connection issue
              </span>
            </span>
            
            <div className={styles.gmailTokenDetails}>
              <span className={styles.gmailTokenEmail}>
                {gmailToken.email}
              </span>
              <span className={styles.gmailTokenError}>
                {getTokenErrorLabel(gmailToken.errorType)}
              </span>
            </div>
            
            {!gmailToken.isResolved && (
              <div className={styles.gmailTokenAction}>
                <a
                  href="/settings/gmail"
                  className={styles.gmailReconnectLink}
                >
                  Reconnect Gmail
                </a>
              </div>
            )}
            
            {gmailToken.isResolved && gmailToken.resolvedAt && (
              <span className={styles.gmailTokenResolved}>
                ✓ Reconnected {formatRelativeTime(gmailToken.resolvedAt)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderUpcomingMeetingsActivity = () => {
    if (!activity.upcomingMeetings) return null;
    return <UpcomingMeetingsStory meetings={activity.upcomingMeetings} compact />;
  };

  // Handle upcoming_meetings as a special case with no wrapper
  if (activity.type === 'upcoming_meetings' && activity.upcomingMeetings) {
    return renderUpcomingMeetingsActivity();
  }

  const itemClassName = isNested
    ? `${styles.activityItem} ${styles.nestedItem}`
      : activity.task
        ? `${styles.activityItem} ${styles.taskItem}`
      : activity.calendarEvent
        ? `${styles.activityItem} ${styles.calendarItem}`
      : activity.birthday
      ? `${styles.activityItem} ${styles.birthdayItem}`
      : activity.security
        ? `${styles.activityItem} ${styles.securityItem}`
        : activity.aiCredits
          ? `${styles.activityItem} ${styles.aiCreditsItem}`
        : activity.gmailToken && !activity.gmailToken.isResolved
          ? `${styles.activityItem} ${styles.gmailTokenItem}`
          : activity.gmailToken
            ? `${styles.activityItem} ${styles.systemItem}`
          : styles.activityItem;

  return (
    <div className={itemClassName}>
      {activity.contact && renderContactActivity()}
      {activity.task && renderTaskActivity()}
      {activity.merge && renderMergeActivity()}
      {activity.calendarEvent && renderCalendarActivity()}
      {activity.birthday && renderBirthdayActivity()}
      {activity.security && renderSecurityActivity()}
      {activity.aiCredits && renderAICreditActivity()}
      {activity.gmailToken && renderGmailTokenActivity()}
    </div>
  );
};

export default ActivityFeedItem;
